import { summariseCompletion, type CompletionSummary } from '@dyeatlas/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '@/lib/api'

export interface RecordPhoto {
  id: string
  filename: string
  url: string
  position: number
}

export interface RecordDetail {
  id: string
  code: string
  kind: 'experiment' | 'reference'
  status: 'draft' | 'finished' | 'in_review' | 'published' | 'rejected' | 'trashed'
  ownerId: string
  completionPct: number
  swatchHex: string | null
  rejectionReason: string | null
  mordantIds: string[]
  connectedExperimentIds: string[]
  referenceId: string | null
  photos: RecordPhoto[]
  completion: CompletionSummary
  /** Set on the Public Database copy of a project experiment; only curators edit these. */
  sourceProjectId: string | null
  projectName: string | null
  inProjectReview: boolean
  /** A project holding this record; such records live there rather than in My Workspace. */
  projectId: string | null
  [field: string]: unknown
}

export type SaveState = 'idle' | 'saving' | 'saved' | 'error'

const AUTOSAVE_DELAY_MS = 900

/**
 * Holds the draft locally and pushes changes to the API on a debounce, so typing
 * stays responsive and a dropped connection costs at most one keystroke batch.
 *
 * Only changed fields are sent. The server returns the authoritative record,
 * including the recomputed swatch and completion percentage.
 */
export function useRecordDraft(recordId: string | undefined) {
  const queryClient = useQueryClient()
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [savedAt, setSavedAt] = useState<Date | null>(null)

  const pending = useRef<Record<string, unknown>>({})
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const query = useQuery({
    queryKey: ['record', recordId],
    queryFn: () => api.get<{ record: RecordDetail }>(`/records/${recordId}`),
    enabled: Boolean(recordId),
  })

  const record = query.data?.record

  // Seed local state once the record arrives, and again whenever the server sends
  // a different record (navigating between drafts reuses this hook).
  const seededFor = useRef<string | null>(null)
  useEffect(() => {
    if (!record || seededFor.current === record.id) return
    seededFor.current = record.id
    setValues({ ...record, morMordantIds: record.mordantIds })
  }, [record])

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch<{ record: RecordDetail }>(`/records/${recordId}`, patch),
    onSuccess: (result) => {
      setSaveState('saved')
      setSavedAt(new Date())
      queryClient.setQueryData(['record', recordId], result)
      // Photos and derived fields come back from the server; local edits win elsewhere.
      setValues((current) => ({
        ...current,
        photos: result.record.photos,
        swatchHex: result.record.swatchHex,
        completionPct: result.record.completionPct,
        code: result.record.code,
        status: result.record.status,
      }))
      void queryClient.invalidateQueries({ queryKey: ['records'] })
    },
    onError: () => setSaveState('error'),
  })

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    const patch = pending.current
    pending.current = {}
    if (Object.keys(patch).length === 0) return Promise.resolve()
    setSaveState('saving')
    return save.mutateAsync(patch).catch(() => undefined)
  }, [save])

  const setField = useCallback(
    (field: string, value: unknown) => {
      setValues((current) => ({ ...current, [field]: value }))
      pending.current[field] = value

      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        void flush()
      }, AUTOSAVE_DELAY_MS)
    },
    [flush],
  )

  // Never leave an edit stranded in the debounce window.
  useEffect(() => {
    const onHide = () => {
      if (Object.keys(pending.current).length > 0) void flush()
    }
    window.addEventListener('beforeunload', onHide)
    document.addEventListener('visibilitychange', onHide)
    return () => {
      window.removeEventListener('beforeunload', onHide)
      document.removeEventListener('visibilitychange', onHide)
      onHide()
    }
  }, [flush])

  const completion = summariseCompletion(values)

  return {
    record,
    values,
    setField,
    setValues,
    flush,
    completion,
    saveState,
    savedAt,
    isLoading: query.isLoading,
    error: query.error instanceof ApiError ? query.error : null,
  }
}

/** Whether the editor accepts changes; mirrors the server's `loadEditable`. */
export function isRecordEditable(record: RecordDetail | undefined): boolean {
  if (!record) return true
  if (record.sourceProjectId) return true
  if (record.inProjectReview) return false
  return ['draft', 'finished', 'rejected'].includes(record.status)
}
